import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

/** In-app document preview.
 *
 *  Receives a single `path` like "/quotes/<id>/pdf". The component:
 *  - Fetches "<path-without-/pdf>/preview" → HTML rendered with WeasyPrint's
 *    template + inlined CSS, and shows it in an iframe via srcDoc. This works
 *    in every browser, unlike PDF blob URLs (Edge / iOS Safari refuse those).
 *  - The "Descargar" button fetches the real PDF (`path`) as a blob and
 *    triggers a file download. */
export default function PdfPreviewDialog({ path, filename, onClose }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const downloadUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!path) { setHtml(""); return; }
    setLoading(true);
    const previewPath = path.replace(/\/pdf$/, "/preview");
    api.get(previewPath, { responseType: "text" })
      .then((r) => { if (!cancelled) setHtml(r.data); })
      .catch((e) => { toast.error(formatApiError(e)); onClose?.(); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (downloadUrlRef.current) {
        URL.revokeObjectURL(downloadUrlRef.current);
        downloadUrlRef.current = null;
      }
    };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    if (!path) return;
    setDownloading(true);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      downloadUrlRef.current = url;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setDownloading(false);
  };

  return (
    <Dialog open={!!path} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0 overflow-hidden" data-testid="pdf-preview">
        <DialogHeader className="px-5 pt-5 pb-3 flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-display text-xl">Vista previa</DialogTitle>
          <Button onClick={download} disabled={!html || downloading} size="sm" data-testid="btn-pdf-download">
            {downloading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}
            Descargar PDF
          </Button>
        </DialogHeader>
        <div className="flex-1 bg-white border-t border-[var(--rule)] relative overflow-auto">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm bg-white/80">
              <Loader2 className="animate-spin mr-2" size={16} /> Generando vista previa…
            </div>
          )}
          {html && (
            <iframe
              srcDoc={html}
              title="Vista previa"
              className="w-full h-full border-0 bg-white"
              data-testid="pdf-iframe"
              sandbox="allow-same-origin"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
